import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api } from "../../../services";
import { StorageContext } from "../../../contexts/StorageContext";
import Grid from "@material-ui/core/Grid";
import { CustomText } from "../../../components/index";
import masks from "../../../utils/masks";
import hooks from "../../../utils/hooks";
import { customModal } from "../../../components/modals/utils";
import Styles from "../styles";
import alerts from "../../../utils/alerts";
import SkeletonLoader from "../../../components/others/SkeletonLoader";
import {
  Input,
  InputDate,
  InputTags,
  InputPhone,
  Select,
} from "../../../components/inputs/newForm";
import {
  postTenantReservationProps,
  putTenantReservationProps,
} from "../../../services/api";
import ChechkInAndOut from "./ChechkInAndOut";

function normalizeDate(val: any): Date | null {
  if (!val) return null;
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  }
  if (val instanceof Date) return val;
  return null;
}

const createTenantSchema = (isEdit: boolean) =>
  z
    .object({
      tenantId: z.string().optional(),
      name: z.string().min(1, "*Campo Obrigatório"),
      property: z
        .array(z.object({ value: z.string(), label: z.string() }))
        .optional(),
      startDate: z.date().nullable().optional(),
      endDate: z.date().nullable().optional(),
      checkInTime: z.string().optional(),
      checkOutTime: z.string().optional(),
      vehiclePlate: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!isEdit) {
        if (!data.property?.length)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "*Campo Obrigatório",
            path: ["property"],
          });
        if (!data.startDate)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "*Campo Obrigatório",
            path: ["startDate"],
          });
        if (!data.endDate)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "*Campo Obrigatório",
            path: ["endDate"],
          });
        if (!data.checkInTime)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "*Campo Obrigatório",
            path: ["checkInTime"],
          });
        if (!data.checkOutTime)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "*Campo Obrigatório",
            path: ["checkOutTime"],
          });
      }
    });

export function GridRegisterTenant(props) {
  const { edit, oldData, placeList, reload, page, onImageUrlFetch } = props;

  const isEdit = Boolean(oldData);

  const tenantSchema = React.useMemo(
    () => createTenantSchema(isEdit),
    [isEdit]
  );

  type TenantFormData = z.infer<ReturnType<typeof createTenantSchema>>;

  const { getData } = useContext<any>(StorageContext);
  const condominiumId = getData("company_type");

  const { loading, call } = hooks.useRequest();
  const [unError, setUnError] = React.useState<any>(false);
  const [storedImage, setStoredImage] = React.useState<string | null>(null);
  const [tenantList, setTenantList] = React.useState<any[]>([]);
  const [loadingTenants, setLoadingTenants] = React.useState(!oldData);
  const [filteredPlaceList, setFilteredPlaceList] = React.useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = React.useState<any>(null);
  const [show, setShow] = React.useState({ plate: false });
  const showRef = React.useRef(show);
  React.useEffect(() => { showRef.current = show; }, [show]);

  const { control, handleSubmit, getValues, setValue, watch } = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      tenantId: "",
      name: oldData?.primaryGuest?.name ?? "",
      property: [],
      startDate: normalizeDate(oldData?.startDate),
      endDate: normalizeDate(oldData?.endDate),
      checkInTime: oldData ? masks.extractHour(oldData?.startDate) : "",
      checkOutTime: oldData ? masks.extractHour(oldData?.endDate) : "",
      vehiclePlate: oldData?.primaryGuest?.vehiclePlate ?? "",
    },
  });

  React.useEffect(() => {
    if (oldData) return;
    api.getTenant({ page: 0, size: 999999, condominiumId }).then((response: any) => {
      if (response?.ok) setTenantList(response.data.content ?? []);
      setLoadingTenants(false);
    });
  }, []);

  const updateShowFromPlace = (placeID: { value: string; label: string }[] | undefined) => {
    if (!placeID?.length) { setShow({ plate: true }); return; }
    const place = placeList?.find((p: any) => p.value === placeID[0].value);
    if (!place?.params) { setShow({ plate: true }); return; }

    const normalize = (v: any) => {
      if (typeof v === "boolean") return v;
      const s = String(v ?? "").toLowerCase().trim();
      return s === "true" || s === "1";
    };

    let plate = true;
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    let inHour = 14, inMin = 0, outHour = 14, outMin = 0;

    place.params.forEach((param: any) => {
      if (param.type === "GUEST_SHEET_VEHICLE_PLATE_ENABLED") plate = normalize(param.value);
      if (param.type === "CHECK_IN_DEFAULT_HOUR") inHour = Number(param.value) || 14;
      if (param.type === "CHECK_IN_DEFAULT_MINUTE") inMin = Number(param.value) || 0;
      if (param.type === "CHECK_OUT_DEFAULT_HOUR") outHour = Number(param.value) || 14;
      if (param.type === "CHECK_OUT_DEFAULT_MINUTE") outMin = Number(param.value) || 0;
    });

    setShow({ plate });
    if (!oldData) {
      setValue("checkInTime", `${pad(inHour)}:${pad(inMin)}`);
      setValue("checkOutTime", `${pad(outHour)}:${pad(outMin)}`);
    }
  };

  const handleTenantSelect = (tenantId: string) => {
    const tenant = tenantList.find((t) => t.id === tenantId);
    if (!tenant) return;
    setSelectedTenant(tenant);
    setValue("name", tenant.name ?? "");
    setValue("property", []);
    setShow({ plate: false });

    const tenantPlaceIds: string[] = (tenant.places ?? [])
      .filter((p: any) => p.type === "NORMAL")
      .map((p: any) => p.id);
    setFilteredPlaceList(
      (placeList ?? []).filter((p: any) => tenantPlaceIds.includes(p.value))
    );
  };

  const handelSubmit = (data: TenantFormData) => {
    if (oldData) {
      const submitData: any = {
        id: oldData?.id,
        placeId: oldData?.place?.id,
        startDate: masks.dateToSubmit(data.startDate) + " " + data.checkInTime!,
        endDate: masks.dateToSubmit(data.endDate) + " " + data.checkOutTime!,
        primaryGuest: {
          name: data.name?.trim(),
          document: oldData?.primaryGuest.document || "",
          email: oldData?.primaryGuest.email || "",
          ...(data.vehiclePlate ? { vehiclePlate: masks.removePlacaMask(data.vehiclePlate) } : {}),
        },
        secondaryGuests: [],
        numGuests: 0,
      }

      call(null, api.patchReservation(submitData), async (response) => {
        if (response.ok) {
          customModal.close();
          reload(page);
          alerts.setErrorModal(
            "Reserva Editada",
            ["Reserva editada com sucesso!"],
            null,
            "Ok, Entendi",
            "#0C396D"
          );
        }
      });
    } else {
      const submitData: Partial<postTenantReservationProps> = {
        placeIds: data.property!.filter((p) => p.value !== "all").map((p) => p.value),
        startDate: masks.dateToSubmit(data.startDate) + " " + data.checkInTime!,
        endDate: masks.dateToSubmit(data.endDate) + " " + data.checkOutTime!,
        primaryGuest: {
          name: data.name?.trim(),
          document: selectedTenant?.cpf ? masks.removeNumberMask(selectedTenant.cpf) : undefined,

          email: selectedTenant?.email || "",

          ...(data.vehiclePlate ? { vehiclePlate: masks.removePlacaMask(data.vehiclePlate) } : {}),
        },
      };
      call(null, api.postTenantReservation(submitData), (response) => {
        if (response.ok) {
          customModal.close();
          reload(page);
          alerts.setErrorModal(
            "Reserva Cadastrada",
            ["Reserva cadastrada com sucesso!"],
            null,
            "Ok, Entendi",
            "#0C396D"
          );
        }
      });
    }
  };

  return (
    <>
      {!oldData && (
        <Grid item xs={12} md={12} lg={4}>
          {loadingTenants ? (
            <div>
              <p
                style={{
                  fontSize: "0.75rem",
                  fontFamily: '"IBMPlexSans SemiBold", "Helvetica", "Arial", sans-serif',
                  color: "#0C396D",
                  padding: "5px",
                  margin: 0,
                }}
              >
                Selecionar Inquilino Cadastrado
              </p>
              <SkeletonLoader height={40} $style={{ borderRadius: 21, marginTop: 12 }} />
            </div>
          ) : (
            <Select
              control={control}
              name="tenantId"
              label="Selecionar Inquilino Cadastrado"
              options={tenantList.map((t) => ({ label: t.name, value: t.id }))}
              onChangeCustom={(val) => {
                if (val) handleTenantSelect(val);
              }}
            />
          )}
        </Grid>
      )}

      {oldData && (
        <Grid item xs={12} md={12} lg={9}>
          <Input control={control} name="name" label="Nome Completo" readOnly />
        </Grid>
      )}

      {!oldData && (
        <Grid item xs={12} md={12} lg={5}>
          <InputTags
            control={control}
            name="property"
            label="Imóvel"
            readOnly={isEdit}
            options={isEdit ? placeList ?? [] : filteredPlaceList}
            multiple={true}
            onChangeCustom={(val) => updateShowFromPlace(val)}
          />
        </Grid>
      )}

      {((isEdit && show.plate) || (selectedTenant && show.plate)) && (
        <Grid item xs={12} md={12} lg={3}>
          <Input control={control} name="vehiclePlate" label="Placa do Veículo" mask={masks.Placa} />
        </Grid>
      )}

      {(isEdit || selectedTenant) && (
        <ChechkInAndOut customGrid={{ xs: 12, md: 12, lg: show.plate ? 9 : 12 }}
          control={control}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          oldData={oldData}
          edit={edit}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          paddingTop: "12px",
          width: "100%",
        }}
      >
        <CustomText
          style={{ whiteSpace: "pre-wrap" }}
          fontSize={1.75}
          textColor="#ff7733"
        >
          {unError && "*Campos Obrigatórios"}
        </CustomText>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Styles.CancelButton
            loading={loading}
            disabled={loading}
            onClick={() => customModal.close()}
            fullWidth={false}
          >
            Cancelar
          </Styles.CancelButton>

          <Styles.RegitryButton
            fullWidth={false}
            loading={loading}
            disabled={loading}
            onClick={() => {
              handleSubmit(
                (data) => {
                  setUnError(false);
                  handelSubmit(data);
                },
                () => setUnError(true)
              )();
            }}
          >
            Salvar
          </Styles.RegitryButton>
        </div>
      </div>
    </>
  );
}
